import React from 'react';
import { useMutation } from '@apollo/client';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { Controller, useForm } from 'react-hook-form';
import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';
import ActionButtons from '../../common/ActionButtons';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import UtcDatePicker from '../../common/utcDatePicker/UtcDatePicker';
import {
  GET_ENTITY_TERMINATION_LEGAL_REVIEW,
  MOVE_ENTITY_TERMINATION_TO_NOTICES_PENDING,
} from '../../../utils/statApi/EntityTerminationApi';

interface IMoveToNoticesPendingPanelProps {
  companyCode: string;
  closePanel: () => void;
}

interface IMoveToNoticesPendingForm {
  noticesPendingDate?: string;
}

const MoveToNoticesPendingPanel: React.FC<IMoveToNoticesPendingPanelProps> = ({
  companyCode,
  closePanel,
}) => {
  const { handleSubmit, control, errors, setValue } = useForm<IMoveToNoticesPendingForm>();

  const [moveToNoticesPending, { loading, error }] = useMutation(
    MOVE_ENTITY_TERMINATION_TO_NOTICES_PENDING,
    {
      refetchQueries: [
        {
          query: GET_ENTITY_TERMINATION_LEGAL_REVIEW,
          variables: { companyCode },
        },
      ],
      onCompleted: () => {
        closePanel();
      },
    },
  );

  const handleSave = (formData: IMoveToNoticesPendingForm): void => {
    moveToNoticesPending({
      variables: {
        input: {
          companyCode,
          noticesPendingDate: formData.noticesPendingDate,
        },
      },
    });
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText="Move to Notices Pending"
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          closePanel={closePanel}
          saveLabel="Move to Notices Pending"
          saveTitle="Move to Notices Pending"
          cancelLabel="Cancel"
          cancelTitle="Cancel"
          handleSubmit={handleSubmit(handleSave)}
          mutationLoading={loading}
        />
      )}
    >
      <LoadingErrorMessage
        loading={loading}
        error={error}
        actionName="moving entity to Notices Pending"
      />
      <Controller
        control={control}
        name="noticesPendingDate"
        rules={{ required: 'Notices Pending date is required.' }}
        render={({ value }): React.ReactElement => (
          <UtcDatePicker
            label="Notices Pending Date"
            isRequired
            value={value}
            isRequiredErrorMessage={errors?.noticesPendingDate?.message as string}
            onSelectDate={(date) => setValue('noticesPendingDate', date)}
          />
        )}
      />
    </CoherencePanel>
  );
};

export default MoveToNoticesPendingPanel;
